import { Button, Checkbox, Form, Input, notification } from "antd";
import { API_URL } from "env";
import { useEffect, useState } from "react";

interface PermissionAtribute {
    id: string;
    module: string;
    action: string;
    moduleDisplayName: string;
}

interface ModuleGroup {
    id: string;
    name: string;
    actions: { id: string; name: string }[];
}

const NewRoleModalContent = ({ onClose }: { onClose: () => void }) => {
    const [form] = Form.useForm();
    const [modules, setModules] = useState<ModuleGroup[]>([]);
    const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const [api, contextHolder] = notification.useNotification();

    async function fetchPermissions() {
        try {
            const response = await fetch(`${API_URL}api/v1/be/permissions`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
            });
            if (!response.ok) throw new Error('Network response was not ok');
            const data = await response.json();
            const perms: PermissionAtribute[] = data.data.items.map((item: any) => ({
                id: String(item.id),
                module: item.module ?? '',
                action: item.action,
                moduleDisplayName: item.module_display ?? '',
            }));
            const grouped = perms.reduce((acc, perm) => {
                if (!acc[perm.module]) {
                    acc[perm.module] = { id: perm.module, name: perm.moduleDisplayName, actions: [] };
                }
                acc[perm.module].actions.push({ id: perm.id, name: perm.action });
                return acc;
            }, {} as Record<string, ModuleGroup>);
            setModules(Object.values(grouped));
        } catch (error) {
            console.error('Error fetching permissions:', error);
        }
    }

    async function createRole(values: { roleName: string; roleDescription: string; isActive: boolean }) {
        setSaving(true);
        try {
            const response = await fetch(`${API_URL}api/v1/be/role`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify({
                    name: values.roleName,
                    display_name: values.roleName,
                    description: values.roleDescription,
                    is_active: values.isActive ?? false,
                })
            });
            if (!response.ok) throw new Error('Failed to create role');
            const data = await response.json();
            const role = data.data;
            await fetch(`${API_URL}api/v1/be/role/assign-permission`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify({
                    role_id: role.id,
                    permission_id: selectedPermissions.map(id => Number(id))
                })
            });
            api.success({ message: 'Role created successfully', placement: 'topRight' });
            form.resetFields();
            setSelectedPermissions([]);
            onClose();
        } catch (error) {
            api.error({ message: 'Failed to create role', placement: 'topRight' });
        } finally {
            setSaving(false);
        }
    }

    useEffect(() => {
        fetchPermissions();
    }, []);

    const togglePermission = (id: string, checked: boolean) => {
        setSelectedPermissions(prev =>
            checked ? [...prev, id] : prev.filter(p => p !== id)
        );
    };

    const toggleModule = (moduleActions: { id: string }[], checked: boolean) => {
        const ids = moduleActions.map(a => a.id);
        setSelectedPermissions(prev =>
            checked
                ? Array.from(new Set([...prev, ...ids]))
                : prev.filter(id => !ids.includes(id))
        );
    };

    return (
        <>
            {contextHolder}
            <Form
                form={form}
                layout="vertical"
                initialValues={{ isActive: false }}
                onFinish={createRole}
                className="space-y-2"
            >
                <div className="grid grid-cols-2 gap-4">
                    <Form.Item
                        name="roleName"
                        label="Role Name"
                        rules={[{ required: true, message: 'Please enter role name' }]}
                    >
                        <Input placeholder="Enter role name" />
                    </Form.Item>
                    <Form.Item name="isActive" valuePropName="checked" label="Status">
                        <Checkbox>Active</Checkbox>
                    </Form.Item>
                </div>

                <Form.Item name="roleDescription" label="Description">
                    <Input.TextArea placeholder="Enter role description" rows={2} />
                </Form.Item>

                <Form.Item label="Assign Permissions">
                    <div className="flex flex-col gap-2 max-h-64 overflow-y-auto pr-1">
                        {modules.map(mod => {
                            const allSelected = mod.actions.every(a => selectedPermissions.includes(a.id));
                            const someSelected = mod.actions.some(a => selectedPermissions.includes(a.id));
                            return (
                                <div key={mod.id} className="border border-border rounded-lg p-3 bg-bg-secondary">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Checkbox
                                            checked={allSelected}
                                            indeterminate={!allSelected && someSelected}
                                            onChange={e => toggleModule(mod.actions, e.target.checked)}
                                        />
                                        <span className="text-sm font-semibold">{mod.name || mod.id}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-3 pl-6">
                                        {mod.actions.map(action => (
                                            <label key={action.id} className="flex items-center gap-1.5 cursor-pointer">
                                                <Checkbox
                                                    checked={selectedPermissions.includes(action.id)}
                                                    onChange={e => togglePermission(action.id, e.target.checked)}
                                                />
                                                <span className="text-sm capitalize text-text-secondary">{action.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Form.Item>

                <div className="flex justify-end gap-2 pt-2 border-t border-border">
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" htmlType="submit" loading={saving}>
                        Create Role
                    </Button>
                </div>
            </Form>
        </>
    );
};

export default NewRoleModalContent;
